import { useMemo } from 'react';
import { useAuthStore } from '../lib/stores/auth-store';
import {
  type PlanType,
  type PlanFeatures,
  type PlanLimits,
  getPlanConfig,
  checkLimit,
  hasFeature,
  getFeatureValue,
  getLimit,
  getUpgradePlan,
  isUnlimited,
  FEATURE_DESCRIPTIONS,
} from '../lib/config/pricing';
import { useOrganizationStore } from '../lib/stores/organization-store';

interface UsageData {
  trucks: number;
  trailers: number;
  drivers: number;
  loadsThisMonth: number;
  customers: number;
  carriers: number;
  documentStorageUsedGB: number;
  smsThisMonth: number;
  emailsThisMonth: number;
}

// Mock usage data - in production this would come from the API
const mockUsageData: UsageData = {
  trucks: 8,
  trailers: 12,
  drivers: 7,
  loadsThisMonth: 45,
  customers: 18,
  carriers: 0,
  documentStorageUsedGB: 1.2,
  smsThisMonth: 35,
  emailsThisMonth: 120,
};

export function usePlan() {
  const { subscription } = useOrganizationStore();
  const planType = (subscription?.plan?.name as PlanType) || 'growth';
  const config = useMemo(() => {
    // Ensure we always return a valid config, fallback to growth if invalid
    try {
      const cfg = getPlanConfig(planType);
      return cfg || getPlanConfig('growth');
    } catch {
      return getPlanConfig('growth');
    }
  }, [planType]);

  return {
    planType: planType || 'growth',
    planName: config?.name || 'Growth',
    price: config?.price ?? 0,
    annualPrice: config?.annualPrice ?? 0,
    description: config?.description || '',
    fleetSize: config?.fleetSize || '1-5 trucks',
    limits: config?.limits ?? {},
    features: config?.features ?? {},
    isCustomPlan: (planType || 'growth') === 'enterprise',
    upgradePlan: getUpgradePlan(planType || 'growth'),
  };
}

export function useFeature<K extends keyof PlanFeatures>(feature: K) {
  const { planType } = usePlan();

  return useMemo(() => {
    const value = getFeatureValue(planType || 'growth', feature);
    const enabled = hasFeature(planType || 'growth', feature);
    const description = FEATURE_DESCRIPTIONS[feature] || '';

    return {
      value,
      enabled,
      description,
      planType: planType || 'growth',
    };
  }, [planType, feature]);
}

export function useLimit<K extends keyof PlanLimits>(limitKey: K) {
  const { planType } = usePlan();

  // In production, this would fetch actual usage from the API
  const usage = mockUsageData;

  return useMemo(() => {
    const safePlanType = planType || 'growth';
    const limit = getLimit(safePlanType, limitKey);
    const unlimited = isUnlimited(limit);

    // Map limit keys to usage data
    const usageMap: Partial<Record<keyof PlanLimits, number>> = {
      maxTrucks: usage.trucks,
      maxTrailers: usage.trailers,
      maxDrivers: usage.drivers,
      maxLoadsPerMonth: usage.loadsThisMonth,
      maxCustomers: usage.customers,
      maxCarriers: usage.carriers,
      maxDocumentStorageGB: usage.documentStorageUsedGB,
      maxSmsPerMonth: usage.smsThisMonth,
      maxEmailPerMonth: usage.emailsThisMonth,
    };

    const current = usageMap[limitKey] ?? 0;
    const status = checkLimit(current, limit);

    return {
      limit,
      current,
      unlimited,
      ...status,
      planType: safePlanType,
    };
  }, [planType, limitKey, usage]);
}

export function useLimitCheck() {
  const { planType } = usePlan();

  return useMemo(() => {
    const safePlanType = planType || 'growth';
    return {
      canAddTruck: (currentCount: number) => {
        const limit = getLimit(safePlanType, 'maxTrucks');
        return checkLimit(currentCount, limit).allowed;
      },
      canAddTrailer: (currentCount: number) => {
        const limit = getLimit(safePlanType, 'maxTrailers');
        return checkLimit(currentCount, limit).allowed;
      },
      canAddDriver: (currentCount: number) => {
        const limit = getLimit(safePlanType, 'maxDrivers');
        return checkLimit(currentCount, limit).allowed;
      },
      canAddLoad: (currentMonthCount: number) => {
        const limit = getLimit(safePlanType, 'maxLoadsPerMonth');
        return checkLimit(currentMonthCount, limit).allowed;
      },
      canAddCustomer: (currentCount: number) => {
        const limit = getLimit(safePlanType, 'maxCustomers');
        return checkLimit(currentCount, limit).allowed;
      },
      canAddCarrier: (currentCount: number) => {
        const limit = getLimit(safePlanType, 'maxCarriers');
        return checkLimit(currentCount, limit).allowed;
      },
      canSendSms: (currentMonthCount: number) => {
        const limit = getLimit(safePlanType, 'maxSmsPerMonth');
        return checkLimit(currentMonthCount, limit).allowed;
      },
      canSendEmail: (currentMonthCount: number) => {
        const limit = getLimit(safePlanType, 'maxEmailPerMonth');
        return checkLimit(currentMonthCount, limit).allowed;
      },
    };
  }, [planType]);
}

export function useFeatureGate(feature: keyof PlanFeatures) {
  const { enabled, description } = useFeature(feature);
  const { upgradePlan, planName } = usePlan();

  return {
    enabled,
    description,
    upgradeRequired: !enabled,
    upgradePlan,
    currentPlan: planName || 'Starter',
    upgradeMessage: !enabled
      ? `Upgrade to ${upgradePlan ? getPlanConfig(upgradePlan)?.name || 'a higher plan' : 'a higher plan'} to unlock ${description?.toLowerCase() || 'this feature'}`
      : null,
  };
}
